/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { accessAPI, TablaFiltrable } from "../../shared";
import { Boton } from "../../shared";

import { Accordion, AccordionTab } from "primereact/accordion";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { confirmDialog } from "primereact/confirmdialog";

const FILTROS = [{ campo: "_empresaText", label: "Empresa", labelPlural: "empresas" }];
const SEARCH_FIELDS = ["_empresaText", "email"];

export default function UsuariosPendientes({ toast }) {
  const [usuarios, setUsuarios] = useState();
  const [loading, setLoading] = useState(true);
  const [aprobando, setAprobando] = useState(null);
  const [selectedUsuario, setSelectedUsuario] = useState(null);

  const navigate = useNavigate();
  const menuRef = useRef(null);

  useEffect(() => {
    cargarPendientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cargarPendientes() {
    accessAPI(
      "GET",
      "admin/usuario/pendientes",
      null,
      (respuesta) => {
        setUsuarios(respuesta);
        setLoading(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg || "Error al cargar usuarios pendientes",
          sticky: true
        });
        setLoading(false);
      }
    );
  }

  function obtenerNombreEmpresa(usuario) {
    const c = usuario.empleadocliente?.cliente;
    if (!c) return "";
    return c.nombrefantasia && c.nombrefantasia !== c.razonsocial
      ? `${c.razonsocial} (${c.nombrefantasia})`
      : c.razonsocial;
  }

  function aprobarUsuario(usuario) {
    const empresa = obtenerNombreEmpresa(usuario);
    const mensajeEmpresa = empresa ? ` para la empresa ${empresa}` : "";
    confirmDialog({
      message: `¿Aprobar al usuario ${usuario.nombre} ${usuario.apellido} (${usuario.email})${mensajeEmpresa}? Se le enviará un correo con instrucciones para crear su contraseña.`,
      header: "Aprobar usuario",
      icon: "pi pi-user-plus",
      acceptLabel: "Aprobar",
      rejectLabel: "Cancelar",
      accept: () => {
        setAprobando(usuario.id);
        accessAPI(
          "PUT",
          `admin/usuario/aprobar/${usuario.id}`,
          null,
          () => {
            setAprobando(null);
            toast.current?.show({
              severity: "success",
              summary: "Usuario aprobado",
              detail: `Se envió el correo a ${usuario.email}`,
              life: 3000
            });
            cargarPendientes();
          },
          (respuesta) => {
            setAprobando(null);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al aprobar usuario",
              sticky: true
            });
          }
        );
      }
    });
  }

  function eliminarUsuarioPendiente(usuario) {
    const empresa = obtenerNombreEmpresa(usuario);
    const mensajeEmpresa = empresa ? ` de la empresa ${empresa}` : "";
    confirmDialog({
      message: `¿Eliminar el usuario pendiente de ${usuario.nombre} ${usuario.apellido}${mensajeEmpresa}? Esto no elimina al empleado, solo cancela la solicitud de usuario.`,
      header: "Eliminar usuario pendiente",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        accessAPI(
          "DELETE",
          `admin/usuario/${usuario.id}`,
          null,
          () => {
            toast.current?.show({
              severity: "success",
              summary: "Usuario pendiente eliminado",
              life: 3000
            });
            cargarPendientes();
          },
          (respuesta) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al eliminar usuario",
              sticky: true
            });
          }
        );
      }
    });
  }

  const menuItems = selectedUsuario
    ? [
        {
          label: "Aprobar usuario",
          icon: "pi pi-check",
          command: () => aprobarUsuario(selectedUsuario)
        },
        {
          label: "Ver empresa",
          icon: "pi pi-building",
          command: () => {
            const clienteId = selectedUsuario.empleadocliente?.cliente?.id;
            if (clienteId) {
              navigate(`/clientes/${clienteId}`);
            }
          },
          disabled: !selectedUsuario.empleadocliente?.cliente?.id
        },
        {
          label: "Eliminar usuario pendiente",
          icon: "pi pi-trash",
          command: () => eliminarUsuarioPendiente(selectedUsuario)
        }
      ]
    : [];

  // Si no hay pendientes y terminó de cargar, no mostrar nada
  if (!loading && (!usuarios || usuarios.length === 0)) {
    return null;
  }

  if (loading) {
    return (
      <div css={{ marginTop: "1em" }}>
        <Skeleton width="100%" height="3.5rem" borderRadius="6px" />
      </div>
    );
  }

  const datos = usuarios.map((u) => ({
    ...u,
    _empresaText: obtenerNombreEmpresa(u)
  }));

  return (
    <>
      <Menu model={menuItems} popup ref={menuRef} />
      <Accordion
        activeIndex={null}
        css={{
          marginTop: "1em",
          "& .p-accordion-header-link": { color: "black" },
          "& .p-toggleable-content": {
            transition: "max-height 0.15s ease"
          }
        }}
      >
        <AccordionTab
          header={`Usuarios pendientes de aprobación (${usuarios.length})`}
        >
          <TablaFiltrable
            value={datos}
            filtros={FILTROS}
            searchFields={SEARCH_FIELDS}
            nombreArchivo="usuarios_pendientes"
            emptyMessage="No hay usuarios pendientes"
          >
            <Column
              header="Nombre"
              body={(u) => `${u.nombre} ${u.apellido}`}
            />
            <Column field="email" header="Email" />
            <Column
              header="Empresa"
              body={(u) => u._empresaText || "-"}
            />
            <Column
              header="Cargo"
              body={(u) => u.empleadocliente?.cargo || "-"}
              style={{ width: "10em" }}
            />
            <Column
              body={(u) => (
                <div css={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5em" }}>
                  {aprobando === u.id && <i className="pi pi-spin pi-spinner" />}
                  <Boton
                    texto
                    negro
                    callback={(e) => {
                      setSelectedUsuario(u);
                      menuRef.current.toggle(e);
                    }}
                  >
                    <i className="pi pi-ellipsis-v" />
                  </Boton>
                </div>
              )}
              style={{ width: "60px", textAlign: "center" }}
            />
          </TablaFiltrable>
        </AccordionTab>
      </Accordion>
    </>
  );
}
